'use client'

import { DragEvent, FC, useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'

import {
  AttentionRoundIcon,
  CloseIcon,
  DocumentIcon,
  UploadIcon,
} from '@/components/icons'

import { FileInputRefProps, HasFileProps, InputProps } from './types'

function preventDisabledDragOver(event: DragEvent<HTMLDivElement>) {
  event.preventDefault()
}

const FileInput: FC<FileInputRefProps> = ({
  label,
  labelElement,
  className,
  field,
  errorMessage,
  accept,
  disabled = false,
  onDisabledAttempt,
}) => {
  const [file, setFile] = useState<File | null>(field?.value || null)

  const removeFile = useCallback(() => {
    setFile(null)
    field?.onChange(null)
  }, [field])

  const onDrop = useCallback(
    (files: File[]) => {
      if (disabled) {
        onDisabledAttempt?.()
        return
      }

      const selectedFile = files[0]
      setFile(selectedFile)
      field?.onChange(selectedFile)
    },
    [disabled, field, onDisabledAttempt],
  )

  const acceptDefault = {
    'application/vnd.ms-excel': ['.xlsx'],
  }

  const dropzone = useDropzone({
    onDrop,
    accept: accept ?? acceptDefault,
    disabled,
    noClick: disabled,
    noDrag: disabled,
  })

  const handleDisabledDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      onDisabledAttempt?.()
    },
    [onDisabledAttempt],
  )

  useEffect(() => {
    setFile(field?.value || null)
  }, [field?.value])

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {!!label && (
        <label htmlFor="dropzone-file" className="prose-body text-black">
          {label}
        </label>
      )}

      {!!labelElement && (
        <div className={disabled ? 'cursor-not-allowed' : undefined}>
          {labelElement}
        </div>
      )}

      {file ? (
        <HasFile
          file={file}
          removeFile={removeFile}
          errorMessage={errorMessage}
        />
      ) : (
        <div
          tabIndex={disabled ? undefined : -1}
          className={
            disabled
              ? 'w-full text-left cursor-not-allowed opacity-60'
              : 'w-full text-left'
          }
          onDragOver={disabled ? preventDisabledDragOver : undefined}
          onDrop={disabled ? handleDisabledDrop : undefined}
        >
          <Input
            label={label}
            dropzone={dropzone}
            className={className}
            errorMessage={errorMessage}
            disabled={disabled}
            onDisabledAttempt={disabled ? onDisabledAttempt : undefined}
          />
        </div>
      )}
    </div>
  )
}

const Input: FC<InputProps> = ({
  dropzone,
  errorMessage,
  disabled = false,
  onDisabledAttempt,
}) => {
  const { getRootProps, getInputProps, isDragActive } = dropzone

  const areaClassName = `
    group flex gap-2 items-center justify-center px-[14px] py-[12px] w-full h-[50px] max-h-[50px]
    ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} bg-white rounded-lg border-dashed border-[1.5px]
    ${errorMessage ? 'border-status-red' : 'border-grayscale-200'}
    ${isDragActive ? 'border-primary-500 border-[2px]' : ''}
  `

  const hintClassName =
    'w-full prose-body text-grayscale-200 group-hover:text-black'

  const fileInput = (
    <input id="dropzone-file" {...getInputProps()} className="hidden" />
  )

  const error = !!errorMessage && (
    <span className="flex gap-1 items-center text-status-red prose-caption">
      <AttentionRoundIcon className="fill-status-red w-4 h-4" />
      {errorMessage}
    </span>
  )

  if (disabled) {
    return (
      <div className="flex flex-col gap-2 w-full">
        <button
          type="button"
          aria-disabled="true"
          onClick={() => onDisabledAttempt?.()}
          className={`${areaClassName} text-left`}
        >
          <UploadIcon className="fill-grayscale-200 group-hover:fill-primary-500" />
          <span className={hintClassName}>
            Clique para enviar ou arraste até aqui
          </span>
        </button>
        {fileInput}
        {error}
      </div>
    )
  }

  return (
    <div {...getRootProps()} className="flex flex-col gap-2 w-full">
      <div className={areaClassName}>
        <UploadIcon className="fill-grayscale-200 group-hover:fill-primary-500" />
        <p className={hintClassName}>
          {isDragActive
            ? 'Solte para adicionar'
            : 'Clique para enviar ou arraste até aqui'}
        </p>
      </div>
      {fileInput}
      {error}
    </div>
  )
}

const HasFile = ({ file, removeFile, errorMessage }: HasFileProps) => {
  return (
    <div className="flex flex-col gap-2 w-full">
      <div
        className={`rounded-lg border w-full bg-grayscale-50 flex justify-center items-center
        ${errorMessage ? 'border-status-red' : 'border-grayscale-200'}`}
      >
        <div className="flex bg-grayscale-50 rounded-lg gap-2 items-center justify-between px-3.5 py-3 w-full max-h-[50px]">
          <DocumentIcon className="fill-primary-500 min-w-max" />
          <span title={file?.name} className="prose-body text-black truncate">
            {file?.name}
          </span>
          <CloseIcon
            className="fill-status-red cursor-pointer min-w-max"
            onClick={removeFile}
          />
        </div>
      </div>
      {!!errorMessage && (
        <span className="flex gap-1 items-center text-status-red prose-caption">
          <AttentionRoundIcon className="fill-status-red w-4 h-4" />
          {errorMessage}
        </span>
      )}
    </div>
  )
}

export default FileInput
